<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import { Input } from "$lib/components/ui/input";
  import { Search } from "lucide-svelte";
  import { cn } from "$lib/utils";
  import { page } from "$app/state";
  import { authStore, listStore, searchStore } from "$lib/stores";
  import ExportModal from "$lib/components/dialogs/searchResultsExportDialog.svelte";
  import ShareButton from "$lib/components/dialogs/shareSearchDialog.svelte";
  // import ConvertToListButton from "$lib/components/search/searchHeader/convertToListButton.svelte";
  import { CREDITS_PER } from "$lib/data/pricing";
  import { Button } from "$lib/components/ui";
  import { FileDown, ChevronRight, LoaderCircle } from "lucide-svelte";
  // import type { SearchInfo } from "$lib/types";
  // import { env, isDevelopment } from "$lib/utility/environment";

  const dispatch = createEventDispatcher();

  let {
    type = "search",
    // currentSearchQuery = "",
    isExportingPremiumCSV,
    className = "",
    isViewedByOwner,
    isLoadingData = true,
    handleFetchMore = () => {},
    handleFindMore = () => {},
    handleFindAll = () => {},
    canExpandSearch = true,
    isSearchInProgress,
    showSignInPrompt,
  } = $props<{
    type?: "search" | "list";
    // currentSearchQuery?: string;
    isExportingPremiumCSV?: boolean;
    className?: string;
    isViewedByOwner?: boolean;
    isLoadingData?: boolean;
    handleFetchMore?: () => void;
    handleFindMore?: () => void;
    handleFindAll?: () => void;
    canExpandSearch?: boolean;
    isSearchInProgress?: boolean;
    showSignInPrompt?: () => void;
  }>();

  const isSearchPage = $derived(page.url.pathname.startsWith("/search"));
  const isListPage = $derived(page.url.pathname.startsWith("/list"));

  let currentRecord = $derived(
    isSearchPage ? searchStore.getSearch(page.params.slug) : isListPage ? listStore.getList(page.params.slug) : null,
  );

  function handleCSVDownload(): Promise<void> {
    return new Promise((resolve) => {
      dispatch("csvDownload");
      resolve();
    });
  }

  function handlePremiumCSVDownload(): Promise<void> {
    let searchId = Number(currentRecord?.id);
    let userId = Number($authStore.user?.id);
    return new Promise((resolve) => {
      dispatch("premiumCsvDownload", { searchId, userId });
      resolve();
    });
  }

  let searchTimeout: ReturnType<typeof setTimeout>;

  const debouncedDispatch = (query: string) => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      dispatch("search", { query });
    }, 25);
  };

  let currentSearchQuery = $state("");

  $effect(() => {
    debouncedDispatch(currentSearchQuery);

    return () => clearTimeout(searchTimeout);
  });

  // function addColumn() {
  //   dispatch("addColumn");
  // }

  let isExportButtonDisabled = $derived(type == "list" || isSearchInProgress || !currentRecord ? true : false);

  // let criteriaToggles = $state<Record<string, boolean>>({});
  // let temporaryCriteria = $state<string[]>([]);
  // const hasActiveCriteriaToggles = $derived(Object.values(criteriaToggles).some((toggle) => toggle === true));
  // let showRerunButton = $derived(temporaryCriteria.length > 0);
  // let currentSearch = $derived(searchStore.getSearch(page.params.slug));

  // $effect(() => {
  //   if (currentSearch?.criteria) {
  //     for (const c of currentSearch.criteria) {
  //       if (!(c in criteriaToggles)) {
  //         criteriaToggles[c] = false;
  //       }
  //     }
  //   }
  // });

  // function toggleCriterion(c: string) {
  //   criteriaToggles[c] = criteriaToggles[c] === false ? true : false;
  //   dispatch("criteriaFilterChanged", { toggles: criteriaToggles });
  // }

  // function handleAllCriteriaToggle() {
  //   // Check if all criteria are currently true
  //   const allTrue = Object.values(criteriaToggles).every((value) => value === true);

  //   // Set all toggles to the opposite of allTrue
  //   for (const key in criteriaToggles) {
  //     criteriaToggles[key] = !allTrue;
  //   }

  //   // Dispatch the event with updated toggle states
  //   dispatch("criteriaFilterChanged", { toggles: criteriaToggles });
  // }

  // function addNewCriterion() {}
</script>

<div class={cn("flex w-full flex-col items-start gap-4 px-6 md:px-4 lg:flex-row", className)}>
  <div class="relative order-2 flex w-full flex-grow items-center justify-start gap-4 md:order-1">
    {#if isViewedByOwner}
      <Button
        onclick={handleFindMore}
        class={cn(
          "flex h-fit w-24 items-center justify-center gap-1.5 !rounded-base border border-theme-text-tertiary bg-theme-background-primary px-1.5 py-1 text-2xs font-normal text-theme-text-primary hover:bg-theme-background-tertiary dark:border-white/20 dark:bg-theme-background-tertiary dark:hover:border-white/50 dark:hover:text-white",
          isSearchInProgress
            ? "pointer-events-none bg-white text-black opacity-50 dark:text-theme-gray-800 dark:hover:bg-theme-background-secondary"
            : "",
          "h-7",
        )}
        aria-label="Find more"
        disabled={!canExpandSearch || isSearchInProgress || isLoadingData}
      >
        {#if isSearchInProgress || isLoadingData}
          <LoaderCircle class="h-5 w-5 animate-spin" />
        {:else}
          <ChevronRight strokeWidth={1} class="max-w-4" />
          Find more
        {/if}
      </Button>
    {/if}
    <div class="relative flex w-full flex-grow items-center justify-start">
      <Search class="absolute left-2.5 h-3.5 w-3.5 text-theme-text-primary" strokeWidth={1.5} />
      <Input
        type="text"
        placeholder="Search by name, title, company..."
        class={cn(
          "!rounded-base border-black pl-8 text-xs",
          "bg-theme-gray-50 dark:bg-white/10",
          "text-theme-text-primary dark:text-white",
          "border-theme-gray-200 dark:border-white/10",
          "placeholder:text-theme-gray-400 dark:placeholder:text-white/40",
          "focus:border-theme-gray-300 focus:ring-0 dark:focus:border-transparent",
          "disabled:opacity-90",
          "h-7",
          "focus:border-theme-text-primary focus-visible:ring-0 focus-visible:ring-theme-text-primary focus-visible:ring-offset-0 dark:focus-visible:border-theme-green dark:focus-visible:ring-theme-green",
        )}
        bind:value={currentSearchQuery}
      />
    </div>

    <!-- const showLoad = $derived(
    hasMoreResultsToLoad && !isLoadingMoreResults && !isExpandingSearch
  ); -->
    <!-- {#if showLoadMoreButton && !isLoadingData} -->
    <!-- {/if} -->
  </div>
  <div class="order-1 flex flex-shrink-0 items-start gap-2 md:order-2">
    <!-- {#if env.isDevelopment()}
      <Button
        variant="outline"
        class={cn(
          "!rounded-base text-2xs text-theme-text-primary dark:bg-theme-background-tertiary dark:hover:border-theme-text-secondary dark:hover:text-theme-text-primary flex h-fit items-center justify-center gap-1.5 px-1.5 py-1 font-medium dark:border-white dark:border-white/20",
          "h-7", "opacity-50"
        )}
        onclick={addColumn}
        aria-label="Add column"
      >
        <Plus strokeWidth={1} class="max-h-4" />
        <span class="font-normal">Add column (test)</span>
      </Button>
    {/if} -->

    {#if currentRecord && currentRecord?.id}
      <ExportModal
        category={currentRecord?.category}
        creditsRequired={CREDITS_PER.EMAIL * currentRecord?.results.length}
        creditsAvailable={$authStore.user?.credits?.total || 0}
        onBasicExport={() => handleCSVDownload()}
        onPremiumExport={() => handlePremiumCSVDownload()}
        enablePremiumExport={isViewedByOwner}
        disabled={isExportButtonDisabled}
        loggedOutCallback={() => {}}
        isProcessingCSV={isExportingPremiumCSV}
      >
        <Button
          variant="outline"
          class={cn(
            "flex h-fit items-center justify-center gap-1.5 !rounded-base px-1.5 py-1 text-2xs font-medium text-theme-text-primary dark:border-white dark:border-white/20 dark:bg-theme-background-tertiary dark:hover:border-theme-text-secondary dark:hover:text-theme-text-primary",
            "h-7",
          )}
          aria-label="Export data"
          disabled={isExportButtonDisabled}
        >
          <FileDown strokeWidth={1} class="max-h-4" />
          <span class="font-normal">Export</span>
        </Button>
      </ExportModal>
    {/if}

    {#if currentRecord?.id && isViewedByOwner}
      <ShareButton
        searchId={currentRecord.id}
        isPublic={currentRecord.isPublic || false}
        disabled={type == "list"}
        className="h-7"
      />
    {/if}
  </div>
</div>
